import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Lock, Inbox, Mail, LogOut, RefreshCw, FileText, Building2, ShoppingCart, Package, Receipt, FolderOpen, Users, FileStack, ScrollText, ShieldCheck, Image as ImageIcon } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { AdminBlog } from "../components/AdminBlog";
import { AdminVendors } from "../components/AdminVendors";
import { AdminItems } from "../components/AdminItems";
import { AdminPurchaseOrders } from "../components/AdminPurchaseOrders";
import { AdminInvoices } from "../components/AdminInvoices";
import { AdminDocuments } from "../components/AdminDocuments";
import { AdminCustomers } from "../components/AdminCustomers";
import { AdminSalesDocuments } from "../components/AdminSalesDocuments";
import { AdminLedger } from "../components/AdminLedger";
import { AdminImages } from "../components/AdminImages";
import { AdminUsers, ForceChangePasswordModal } from "../components/AdminUsers";
import { API_BASE } from "../DataContext";

// Tab-to-group mapping — controls which tabs are visible based on user.groups
const TAB_GROUPS = {
  enquiries: null, subscribers: null,       // always visible
  blog: "content", images: "content",
  vendors: "procurement", items: "procurement", "purchase-orders": "procurement", invoices: "procurement", documents: "procurement",
  customers: "customer_portal", "sales-docs": "customer_portal", ledger: "customer_portal",
  users: "__super__",
};

const SUBJECT_LABELS = { product: "Product Enquiry", dealer: "Dealership", career: "Career", support: "Support", other: "Other" };

const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function LoginGate({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      sessionStorage.setItem("baishalii_admin_token", data.token);
      sessionStorage.setItem("baishalii_admin_user", JSON.stringify(data.user));
      onLogin(data.token, data.user);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl border border-slate-100 shadow-lg p-8" data-testid="admin-login-form">
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-sky-500 to-[#0a2a5e] flex items-center justify-center mb-5">
          <Lock className="h-6 w-6 text-white" />
        </div>
        <h1 className="text-2xl font-black text-[#0a2a5e] mb-1">Admin Sign in</h1>
        <p className="text-sm text-slate-500 mb-6">Enter your admin credentials.</p>
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email-input" className="mb-3" />
        <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password-input" />
        {error && <p className="text-sm text-red-600 mt-2" data-testid="admin-login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full mt-4 bg-gradient-to-r from-sky-500 to-[#0a2a5e] hover:opacity-90 text-white rounded-full h-11 font-semibold">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("baishalii_admin_token"));
  const [user, setUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("baishalii_admin_user") || "null"); }
    catch { return null; }
  });
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState(null);
  const [subscribers, setSubscribers] = useState(null);

  const logout = () => {
    sessionStorage.removeItem("baishalii_admin_token");
    sessionStorage.removeItem("baishalii_admin_user");
    setToken(null); setUser(null);
    setEnquiries(null); setSubscribers(null);
  };

  // Refresh user info from server on mount (handles must_change_password state change)
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/admin/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((u) => { setUser(u); sessionStorage.setItem("baishalii_admin_user", JSON.stringify(u)); })
      .catch(() => {});
  }, [token]);

  const canSee = (tabKey) => {
    const group = TAB_GROUPS[tabKey];
    if (!group) return true;
    if (!user) return true; // legacy token (no user info) → show everything
    if (user.role === "super_admin") return true;
    if (group === "__super__") return false;
    return (user.groups || []).includes(group);
  };

  const visibleTabs = useMemo(() => ["enquiries", "subscribers", "blog", "images", "vendors", "items", "purchase-orders", "invoices", "documents", "customers", "sales-docs", "ledger", "users"].filter(canSee), [user]); // eslint-disable-line

  // If current tab becomes invisible after user info loads, snap to first visible
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.includes(tab)) setTab(visibleTabs[0]);
  }, [visibleTabs, tab]);

  const load = useCallback(async (tkn) => {
    const headers = { Authorization: `Bearer ${tkn}` };
    const [er, sr] = await Promise.all([
      fetch(`${API_BASE}/api/admin/enquiries`, { headers }),
      fetch(`${API_BASE}/api/admin/subscribers`, { headers })
    ]);
    if (er.status === 401 || sr.status === 401) {
      logout();
      return;
    }
    setEnquiries(await er.json());
    setSubscribers(await sr.json());
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  if (!token) {
    return (
      <div className="pt-[88px] md:pt-[112px] bg-slate-50 min-h-screen">
        <LoginGate onLogin={(t, u) => { setToken(t); setUser(u); }} />
      </div>
    );
  }

  // Force password change on first login
  if (user && user.must_change_password) {
    return (
      <div className="pt-[88px] md:pt-[112px] bg-slate-50 min-h-screen">
        <ForceChangePasswordModal token={token} onDone={() => setUser({ ...user, must_change_password: false })} />
      </div>
    );
  }

  return (
    <div className="pt-[88px] md:pt-[112px] bg-slate-50 min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-[#0a2a5e]">Admin Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">Leads, blog, vendors and purchase orders.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => load(token)} data-testid="admin-refresh-btn" className="rounded-full">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
            <Button variant="outline" onClick={logout} data-testid="admin-logout-btn" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto -mx-6 px-6 scrollbar-thin">
          {canSee("enquiries") && <button
            onClick={() => setTab("enquiries")}
            data-testid="admin-tab-enquiries"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "enquiries" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <Inbox className="h-4 w-4" /> Enquiries {enquiries && <span className="ml-1 text-xs opacity-75">({enquiries.length})</span>}
          </button>}
          {canSee("subscribers") && <button
            onClick={() => setTab("subscribers")}
            data-testid="admin-tab-subscribers"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "subscribers" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <Mail className="h-4 w-4" /> Subscribers {subscribers && <span className="ml-1 text-xs opacity-75">({subscribers.length})</span>}
          </button>}
          {canSee("blog") && <button
            onClick={() => setTab("blog")}
            data-testid="admin-tab-blog"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "blog" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <FileText className="h-4 w-4" /> Blog Posts
          </button>}
          {canSee("images") && <button
            onClick={() => setTab("images")}
            data-testid="admin-tab-images"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "images" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <ImageIcon className="h-4 w-4" /> Images
          </button>}
          {canSee("vendors") && <button
            onClick={() => setTab("vendors")}
            data-testid="admin-tab-vendors"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "vendors" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <Building2 className="h-4 w-4" /> Vendors
          </button>}
          {canSee("items") && <button
            onClick={() => setTab("items")}
            data-testid="admin-tab-items"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "items" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <Package className="h-4 w-4" /> Items
          </button>}
          {canSee("purchase-orders") && <button
            onClick={() => setTab("purchase-orders")}
            data-testid="admin-tab-po"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "purchase-orders" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <ShoppingCart className="h-4 w-4" /> Purchase Orders
          </button>}
          {canSee("invoices") && <button
            onClick={() => setTab("invoices")}
            data-testid="admin-tab-invoices"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "invoices" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <Receipt className="h-4 w-4" /> Invoices
          </button>}
          {canSee("documents") && <button
            onClick={() => setTab("documents")}
            data-testid="admin-tab-documents"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "documents" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <FolderOpen className="h-4 w-4" /> Documents
          </button>}
          {canSee("customers") && <button
            onClick={() => setTab("customers")}
            data-testid="admin-tab-customers"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "customers" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <Users className="h-4 w-4" /> Customers
          </button>}
          {canSee("sales-docs") && <button
            onClick={() => setTab("sales-docs")}
            data-testid="admin-tab-sales-docs"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "sales-docs" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <FileStack className="h-4 w-4" /> Sales Documents
          </button>}
          {canSee("ledger") && <button
            onClick={() => setTab("ledger")}
            data-testid="admin-tab-ledger"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "ledger" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <ScrollText className="h-4 w-4" /> Ledger
          </button>}
          {canSee("users") && <button
            onClick={() => setTab("users")}
            data-testid="admin-tab-users"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${tab === "users" ? "bg-[#0a2a5e] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-sky-50"}`}
          >
            <ShieldCheck className="h-4 w-4" /> Users
          </button>}
        </div>

        {tab === "blog" && canSee("blog") && <AdminBlog token={token} onUnauthorized={logout} />}
        {tab === "images" && canSee("images") && <AdminImages token={token} onUnauthorized={logout} />}
        {tab === "vendors" && canSee("vendors") && <AdminVendors token={token} onUnauthorized={logout} />}
        {tab === "items" && canSee("items") && <AdminItems token={token} onUnauthorized={logout} />}
        {tab === "purchase-orders" && canSee("purchase-orders") && <AdminPurchaseOrders token={token} onUnauthorized={logout} />}
        {tab === "invoices" && canSee("invoices") && <AdminInvoices token={token} onUnauthorized={logout} />}
        {tab === "documents" && canSee("documents") && <AdminDocuments token={token} onUnauthorized={logout} />}
        {tab === "customers" && canSee("customers") && <AdminCustomers token={token} onUnauthorized={logout} />}
        {tab === "sales-docs" && canSee("sales-docs") && <AdminSalesDocuments token={token} onUnauthorized={logout} />}
        {tab === "ledger" && canSee("ledger") && <AdminLedger token={token} onUnauthorized={logout} />}
        {tab === "users" && canSee("users") && <AdminUsers token={token} onUnauthorized={logout} />}

        {tab === "enquiries" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto" data-testid="admin-enquiries-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-3.5 font-semibold">Date</th>
                  <th className="px-5 py-3.5 font-semibold">Name</th>
                  <th className="px-5 py-3.5 font-semibold">Email</th>
                  <th className="px-5 py-3.5 font-semibold">Phone</th>
                  <th className="px-5 py-3.5 font-semibold">Subject</th>
                  <th className="px-5 py-3.5 font-semibold">Message</th>
                </tr>
              </thead>
              <tbody>
                {!enquiries && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">Loading...</td></tr>}
                {enquiries && enquiries.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400">No enquiries yet.</td></tr>}
                {enquiries && enquiries.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-sky-50/50 align-top">
                    <td className="px-5 py-3.5 whitespace-nowrap text-slate-500">{fmtDate(e.created_at)}</td>
                    <td className="px-5 py-3.5 font-semibold text-[#0a2a5e]">{e.name}</td>
                    <td className="px-5 py-3.5"><a href={`mailto:${e.email}`} className="text-sky-600 hover:underline">{e.email}</a></td>
                    <td className="px-5 py-3.5 whitespace-nowrap">{e.phone || "—"}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap">{SUBJECT_LABELS[e.subject] || e.subject || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-600 max-w-md">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "subscribers" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto max-w-2xl" data-testid="admin-subscribers-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-3.5 font-semibold">Date</th>
                  <th className="px-5 py-3.5 font-semibold">Email</th>
                </tr>
              </thead>
              <tbody>
                {!subscribers && <tr><td colSpan={2} className="px-5 py-8 text-center text-slate-400">Loading...</td></tr>}
                {subscribers && subscribers.length === 0 && <tr><td colSpan={2} className="px-5 py-8 text-center text-slate-400">No subscribers yet.</td></tr>}
                {subscribers && subscribers.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                    <td className="px-5 py-3.5 whitespace-nowrap text-slate-500">{fmtDate(s.created_at)}</td>
                    <td className="px-5 py-3.5 font-semibold text-[#0a2a5e]">{s.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
